import React, { useEffect, useState, useMemo } from 'react';
import {
  Users, Car, Receipt, Wallet, AlertCircle, UserPlus, FilePlus, ClipboardList,
  Calendar as CalIcon, Package, FileText, MapPin, Search, RefreshCw, AlertOctagon,
  TrendingUp, Crown, Clock, Sparkles, Fingerprint, ArrowUpRight, Wrench, ShieldCheck,
  PlayCircle, PauseCircle, CheckCircle2, PackageCheck, Gauge, Plus, Tag, BadgeCheck, Repeat, Mail,
} from 'lucide-react';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { isApkExempt } from '../lib/apk';
import { toast } from 'sonner';
import { carsysApi } from '../api/carsysApi';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { IntegrationsStatusWidget } from '../components/IntegrationsStatusWidget';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const eurFull = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const daysUntil = (d) => Math.ceil((new Date(d) - new Date()) / 86400000);
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };

const AUDIT_FILTERS = [
  ['alle', 'Alles'], ['login', 'Login'], ['klant', 'Klant'], ['factuur', 'Factuur'], ['bon', 'Bon'], ['voertuig', 'Voertuig'], ['werkorder', 'Werkorder'],
];

export default function Dashboard({ onNavigate }) {
  const { klanten, klantAutos: alleAutos, bonnen, meldingen, stats, planning, handelAutos, rdwLookup, vinLookup, adresLookup, refreshMeldingen, refreshAll, getAudit } = useData();
  const [audit, setAudit] = useState([]);
  const [auditFilter, setAuditFilter] = useState('alle');
  const [recalls, setRecalls] = useState([]);
  const [lookupModal, setLookupModal] = useState(null);
  const [apkRefreshing, setApkRefreshing] = useState(false);
  const { t, lang } = useT();
  const loc = lang === 'nl' ? 'nl-NL' : lang === 'en' ? 'en-GB' : 'nl-NL';

  const refreshApkFromRdw = async () => {
    if (apkRefreshing) return;
    setApkRefreshing(true);
    try {
      const res = await carsysApi.refreshApkRdw();
      await refreshAll(true);
      if (res.updated > 0) {
        toast.success(t(`${res.updated} APK-datum(s) bijgewerkt via RDW`));
      } else {
        toast.success(t(`Alle ${res.checked} kentekens gecontroleerd — alles up-to-date`));
      }
    } catch (e) {
      toast.error(t('APK-check mislukt. Probeer later opnieuw.'));
    } finally {
      setApkRefreshing(false);
    }
  };

  useEffect(() => { getAudit().then(setAudit).catch(() => {}); }, [getAudit]);
  useEffect(() => { carsysApi.getRecalls().then(setRecalls).catch(() => {}); }, []);
  const [herhaal, setHerhaal] = useState([]);
  useEffect(() => { carsysApi.getHerhaalfacturen().then(setHerhaal).catch(() => {}); }, []);
  const aankomendHerhaal = useMemo(() => {
    const grens = new Date(); grens.setDate(grens.getDate() + 7);
    return (herhaal || [])
      .filter(h => h.actief && h.volgendeDatum && new Date(h.volgendeDatum) <= grens && (!h.eindDatum || new Date(h.eindDatum) >= new Date(h.volgendeDatum)))
      .map(h => {
        const excl = (h.regels || []).reduce((s, r) => s + (Number(r.aantal) || 0) * (Number(r.prijs) || 0), 0);
        const kw = Number(h.kortingWaarde) || 0;
        const korting = h.kortingType === 'percentage' ? excl * Math.min(100, kw) / 100 : Math.min(excl, kw);
        return { ...h, verwacht: Math.max(0, excl - korting) * 1.21 };
      })
      .sort((a, b) => new Date(a.volgendeDatum) - new Date(b.volgendeDatum));
  }, [herhaal]);
  const verwachtHerhaalTotaal = aankomendHerhaal.reduce((s, h) => s + h.verwacht, 0);
  const klantNaamById = (id) => (klanten.find(k => k.id === id) || {}).naam || '—';

  const today = new Date().toISOString().slice(0, 10);
  const omzetData = stats.omzetPerMaand || [];
  const maxOmzet = Math.max(1, ...omzetData.map(o => o.omzet || 0));
  const omzetKpi = stats.omzetKpi || { vandaag: 0, deze_week: 0, deze_maand: 0, dit_jaar: 0 };
  const topKlanten = stats.topKlanten || [];
  const voorraadAlerts = stats.voorraadAlerts || [];
  const vandaagPlanning = stats.vandaagPlanning || [];
  const aanvragen = (planning || []).filter(p => p.status === 'Aangevraagd');
  const openstaandBonnen = stats.openstaand ?? bonnen.filter(b => b.openstaand > 0).length;
  const openstaandBedrag = stats.openstaandBedrag ?? bonnen.reduce((s, b) => s + (b.openstaand || 0), 0);

  const werkordersOpen = bonnen.filter(b => b.type === 'Werkbon' && b.status !== 'Betaald' && b.werkStatus !== 'Opgehaald');
  const offertes = bonnen.filter(b => b.type === 'Offerte');
  const offertesOmgezet = offertes.filter(b => b.omgezetNaar || ['Omgezet', 'Akkoord', 'Gefactureerd'].includes(b.status)).length;
  const offerteConversiePct = offertes.length ? Math.round((offertesOmgezet / offertes.length) * 100) : 0;
  const [weekSummaryBusy, setWeekSummaryBusy] = useState(false);
  const sendWeekSummary = async () => {
    if (weekSummaryBusy) return;
    setWeekSummaryBusy(true);
    try {
      const r = await carsysApi.sendWeeklySummary();
      if (r.sent) toast.success(t('Weekoverzicht gemaild'), { description: r.aan });
      else toast.error(t('Weekoverzicht niet verstuurd', 'Summary not sent'), { description: r.reason || r.status });
    } catch (e) { toast.error(t('Versturen mislukt')); }
    finally { setWeekSummaryBusy(false); }
  };
  const wsSource = planning || [];
  const ws = {
    open: wsSource.filter(p => !p.werkStatus || p.werkStatus === 'Niet gestart').length,
    bezig: wsSource.filter(p => p.werkStatus === 'Bezig').length,
    wacht: wsSource.filter(p => p.werkStatus === 'Wacht op onderdelen').length,
    klaar: wsSource.filter(p => p.werkStatus === 'Klaar').length,
    opgehaald: wsSource.filter(p => p.werkStatus === 'Opgehaald').length,
  };

  const klantAutos = useMemo(() => (alleAutos || []).filter(a => !a.gearchiveerd), [alleAutos]);
  const apkAlerts = useMemo(() => klantAutos
    .filter(a => !isApkExempt(a))
    .map(a => ({ ...a, dagen: daysUntil(a.apkVervalt), klantNaam: klanten.find(k => k.id === a.klantId)?.naam || '' }))
    .filter(a => Number.isFinite(a.dagen) && a.dagen <= 60)
    .sort((a, b) => a.dagen - b.dagen), [klantAutos, klanten]);
  const apk30 = apkAlerts.filter(a => a.dagen <= 30);

  const onderhoudOverdue = useMemo(() => klantAutos.filter(a => {
    const im = parseInt(a.onderhoudIntervalMaanden) || 0;
    const ik = parseInt(a.onderhoudIntervalKm) || 0;
    const dateOver = a.laatsteOnderhoud && im && addMonths(a.laatsteOnderhoud, im) < new Date();
    const kmOver = a.laatsteOnderhoudKm && ik && ((parseInt(a.laatsteOnderhoudKm) + ik) - (parseInt(a.kmStand) || 0)) < 0;
    return dateOver || kmOver;
  }), [klantAutos]);

  const recallMatches = useMemo(() => {
    let n = 0;
    recalls.forEach(r => { n += klantAutos.filter(a => a.merk && r.merk && a.merk.toLowerCase().includes(r.merk.toLowerCase()) && (!r.model || (a.model || '').toLowerCase().includes(r.model.toLowerCase()))).length; });
    return n;
  }, [recalls, klantAutos]);

  // Action Center
  const acties = [
    { key: 'unpaid', icon: Wallet, label: t('Onbetaalde facturen', 'Onbetaalde facturen'), count: openstaandBonnen, lvl: 'red', target: 'betalingen' },
    { key: 'apk', icon: ShieldCheck, label: t('APK binnen 30 dagen', 'APK binnen 30 dagen'), count: apk30.length, lvl: 'red', target: 'apk' },
    { key: 'wacht', icon: PauseCircle, label: t('Wacht op onderdelen', 'Wacht op onderdelen'), count: ws.wacht, lvl: 'red', target: 'planning' },
    { key: 'open', icon: Wrench, label: t('Open werkorders', 'Open werkorders'), count: werkordersOpen.length, lvl: 'orange', target: 'werkkaart' },
    { key: 'pickup', icon: PackageCheck, label: t('Klaar voor ophalen', 'Klaar voor ophalen'), count: ws.klaar, lvl: 'orange', target: 'planning' },
    { key: 'stock', icon: Package, label: t('Lage voorraad', 'Lage voorraad'), count: voorraadAlerts.length, lvl: 'orange', target: 'onderdelen' },
    { key: 'requests', icon: CalIcon, label: t('Nieuwe online aanvragen', 'Nieuwe online aanvragen'), count: aanvragen.length, lvl: 'orange', target: 'planning' },
  ].filter(a => a.count > 0);

  const auditFiltered = audit.filter(a => auditFilter === 'alle' || (`${a.actie} ${a.detail}`).toLowerCase().includes(auditFilter));

  return (
    <div data-testid="dashboard-page">
      <div className="cs-page-header">
        <div>
          <h2>{t('dash.title')}</h2>
          <p>{new Date().toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <button data-testid="btn-refresh-meldingen" className="cs-btn" onClick={async () => {
          try { const r = await refreshMeldingen(); toast.success(`${r.count} meldingen ververst`); } catch { toast.error(t('Verversen mislukt')); }
        }}><RefreshCw size={14} /> {t('dash.refresh_alerts')}</button>
      </div>

      <OnboardingChecklist onNavigate={onNavigate} />

      <div style={{ marginBottom: 20 }}>
        <IntegrationsStatusWidget onNavigate={onNavigate} />
      </div>

      {/* SECTION 1 — ACTION CENTER */}
      <div className="cs-card padded" data-testid="action-center" style={{ marginBottom: 16, borderLeft: '4px solid #2563eb' }}>
        <h3 className="cs-card-title"><AlertCircle size={16} /> {t('Actiecentrum — wat moet er vandaag gebeuren?', 'Actiecentrum — wat moet er vandaag gebeuren?')}</h3>
        {acties.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 4px', color: '#15803d', fontWeight: 600 }} data-testid="action-allclear">
            <CheckCircle2 size={20} /> {t('Alles onder controle — geen openstaande acties.', 'Alles onder controle — geen openstaande acties.')}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
            {acties.map(a => {
              const c = a.lvl === 'red' ? { bg: '#fef2f2', b: '#fecaca', fg: '#b91c1c' } : { bg: '#fff7ed', b: '#fed7aa', fg: '#c2410c' };
              const Icon = a.icon;
              return (
                <button key={a.key} data-testid={`action-${a.key}`} onClick={() => onNavigate(a.target)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: 'pointer', border: `1px solid ${c.b}`, background: c.bg, borderRadius: 12, padding: '12px 14px' }}>
                  <span style={{ width: 38, height: 38, borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.fg, flexShrink: 0 }}><Icon size={19} /></span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 22, fontWeight: 800, color: c.fg, lineHeight: 1 }}>{a.count}</span>
                    <span style={{ display: 'block', fontSize: 12, color: '#475569', marginTop: 2 }}>{a.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* SECTION 2 — TODAY'S GARAGE */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }} data-testid="todays-garage">
        <MiniStat icon={CalIcon} label={t('Afspraken vandaag', 'Afspraken vandaag')} value={vandaagPlanning.length} accent="#0ea5e9" onClick={() => onNavigate('planning')} testid="ts-afspraken" />
        <MiniStat icon={Wrench} label={t('Open werkorders', 'Open werkorders')} value={werkordersOpen.length} accent="#6366f1" onClick={() => onNavigate('werkkaart')} testid="ts-werkorders" />
        <MiniStat icon={Users} label={t('Klanten', 'Klanten')} value={stats.klanten ?? klanten.length} accent="#8b5cf6" onClick={() => onNavigate('klanten')} testid="ts-klanten" />
        <MiniStat icon={Car} label={t('Voertuigen', 'Voertuigen')} value={stats.voertuigen ?? klantAutos.length} accent="#0d9488" onClick={() => onNavigate('autohandel')} testid="ts-voertuigen" />
        <MiniStat icon={Tag} label={t('Handel in voorraad', 'Handel in voorraad')} value={stats.handelVoorraad ?? handelAutos.filter(a => a.status === 'Voorraad').length} hint={(stats.handelMargeVoorraad ?? 0) ? `${t('Marge', 'Marge')} ${eur(stats.handelMargeVoorraad)}` : ''} accent="#3b82f6" onClick={() => onNavigate('autohandel')} testid="ts-handel-voorraad" />
        <MiniStat icon={BadgeCheck} label={t('Handel verkocht', 'Handel verkocht')} value={stats.handelVerkocht ?? handelAutos.filter(a => a.status === 'Verkocht').length} hint={(stats.handelVerkochtDezeMaand ?? 0) ? `${stats.handelVerkochtDezeMaand} ${t('deze maand', 'deze maand')}` : ''} accent="#16a34a" onClick={() => onNavigate('autohandel')} testid="ts-handel-verkocht" />
        <MiniStat icon={Wallet} label={t('Omzet vandaag', 'Omzet vandaag')} value={eur(omzetKpi.vandaag)} accent="#22c55e" onClick={() => onNavigate('betalingen')} testid="ts-omzet-vandaag" />
        <MiniStat icon={TrendingUp} label={t('Omzet deze maand', 'Omzet deze maand')} value={eur(omzetKpi.deze_maand)} accent="#f59e0b" onClick={() => onNavigate('betalingen')} testid="ts-omzet-maand" />
        <MiniStat icon={AlertCircle} label={t('Openstaand bedrag', 'Openstaand bedrag')} value={eur(openstaandBedrag)} accent="#dc2626" onClick={() => onNavigate('betalingen')} testid="ts-openstaand" />
      </div>

      <div className="cs-two-col">
        {/* SECTION 3 — TODAY'S PLANNING */}
        <div className="cs-card padded" data-testid="card-planning-vandaag">
          <h3 className="cs-card-title"><CalIcon size={16} /> {t('Planning vandaag', 'Planning vandaag')} ({vandaagPlanning.length})</h3>
          {vandaagPlanning.length === 0 ? (
            <div className="cs-empty">{t('Geen afspraken vandaag.')}
              <div style={{ marginTop: 8 }}><button data-testid="btn-empty-planning" className="cs-btn primary" onClick={() => onNavigate('planning')}><Plus size={14} /> {t('Afspraak maken', 'Afspraak maken')}</button></div>
            </div>
          ) : (
            <ul className="apk-list">
              {vandaagPlanning.map(p => (
                <li key={p.id} data-testid={`today-appt-${p.id}`}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: 13, minWidth: 42 }}>{p.start ? new Date(p.start).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{p.klant || p.titel || t('Afspraak')}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)' }}>{p.kenteken ? `${p.kenteken} · ` : ''}{p.titel || ''}</div>
                  </div>
                  <button className="cs-btn ghost" title={t('Start werkorder', 'Start werkorder')} onClick={() => onNavigate('bonnen')} data-testid={`today-werkorder-${p.id}`}><Wrench size={14} /></button>
                  <button className="cs-btn ghost" title={t('Open planning', 'Open planning')} onClick={() => onNavigate('planning')}><ArrowUpRight size={14} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* SECTION 4 — WORKSHOP STATUS */}
        <div className="cs-card padded" data-testid="workshop-status">
          <h3 className="cs-card-title"><Gauge size={16} /> {t('Werkplaatsstatus', 'Werkplaatsstatus')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(92px, 1fr))', gap: 8 }}>
            <WsCard icon={ClipboardList} label={t('Open', 'Open')} value={ws.open} c="#64748b" />
            <WsCard icon={PlayCircle} label={t('Bezig', 'Bezig')} value={ws.bezig} c="#2563eb" />
            <WsCard icon={PauseCircle} label={t('Wacht op onderdelen', 'Wacht op onderdelen')} value={ws.wacht} c="#dc2626" />
            <WsCard icon={PackageCheck} label={t('Klaar voor ophalen', 'Klaar voor ophalen')} value={ws.klaar} c="#16a34a" />
            <WsCard icon={CheckCircle2} label={t('Opgehaald', 'Opgehaald')} value={ws.opgehaald} c="#7c3aed" />
          </div>
        </div>
      </div>

      {aankomendHerhaal.length > 0 && (
        <div className="cs-card padded" data-testid="card-aankomende-herhaalfacturen" style={{ marginTop: 14, borderLeft: '4px solid #16a34a' }}>
          <h3 className="cs-card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><Repeat size={16} /> {t('Aankomende herhaalfacturen (7 dagen)', 'Aankomende herhaalfacturen (7 dagen)')}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }} data-testid="herhaal-verwacht-totaal">{t('Verwacht', 'Verwacht')}: {eur(verwachtHerhaalTotaal)}</span>
          </h3>
          <table className="cs-table" style={{ marginTop: 4 }}>
            <tbody>
              {aankomendHerhaal.slice(0, 6).map(h => (
                <tr key={h.id} data-testid={`dash-herhaal-${h.id}`} style={{ cursor: 'pointer' }} onClick={() => onNavigate('herhaalfacturen')}>
                  <td style={{ fontWeight: 600 }}>{klantNaamById(h.klantId)}{h.omschrijving ? <span style={{ display: 'block', fontSize: 11, color: 'var(--cs-text-muted)', fontWeight: 400 }}>{h.omschrijving}</span> : null}</td>
                  <td style={{ color: 'var(--cs-text-muted)', fontSize: 12.5 }}><CalIcon size={12} style={{ verticalAlign: -1, marginRight: 4 }} />{new Date(h.volgendeDatum).toLocaleDateString('nl-NL')}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(h.verwacht)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <button className="cs-btn ghost" data-testid="dash-herhaal-link" onClick={() => onNavigate('herhaalfacturen')}>{t('Beheer herhaalfacturen', 'Beheer herhaalfacturen')} <ArrowUpRight size={13} /></button>
          </div>
        </div>
      )}

      <div className="cs-two-col" style={{ marginTop: 14 }}>
        {/* SECTION 5 — REVENUE & BUSINESS */}
        <div className="cs-card padded" data-testid="card-omzet-chart">
          <h3 className="cs-card-title"><TrendingUp size={16} /> {t('Omzet per maand (€) - laatste 12 mnd')}</h3>
          {omzetData.every(o => (o.omzet || 0) === 0) ? (
            <div className="cs-empty" style={{ padding: '24px 8px' }}>{t('Nog geen omzet geregistreerd.')}
              <div style={{ marginTop: 10 }}><button data-testid="btn-empty-new-bon" className="cs-btn primary" onClick={() => onNavigate('bonnen')}><FilePlus size={14} /> {t('Nieuwe bon')}</button></div>
            </div>
          ) : (
            <div className="cs-chart">
              {omzetData.map((m, i) => (
                <div key={i} className="bar-wrap" title={eurFull(m.omzet)} onClick={() => onNavigate('betalingen')}>
                  <div className="bar" style={{ height: `${Math.max(2, (m.omzet / maxOmzet) * 100)}%` }}></div>
                  <div className="label">{m.maand}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginTop: 14, borderTop: '1px solid var(--cs-border)', paddingTop: 12 }}>
            <BizStat label={t('Gem. factuurwaarde', 'Gem. factuurwaarde')} value={eur(stats.gemFactuur ?? (omzetKpi.dit_jaar && stats.facturenDitJaar ? omzetKpi.dit_jaar / stats.facturenDitJaar : 0))} />
            <BizStat label={t('Omzet dit jaar', 'Omzet dit jaar')} value={eur(omzetKpi.dit_jaar)} />
            <div data-testid="offerte-conversie" onClick={() => onNavigate('bonnen')} style={{ cursor: 'pointer' }}>
              <BizStat label={t('Offerte-conversie', 'Offerte-conversie')} value={`${offerteConversiePct}%`} />
              <div style={{ fontSize: 11, color: 'var(--cs-text-muted)', marginTop: 2 }}>{offertesOmgezet}/{offertes.length} {t('omgezet', 'omgezet')}</div>
            </div>
            <BizStat label={t('Topklant dit jaar', 'Topklant dit jaar')} value={topKlanten[0]?.naam || '—'} small />
          </div>
        </div>

        {/* SECTION 6 — VEHICLE ALERTS */}
        <div className="cs-card padded" data-testid="vehicle-alerts">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <h3 className="cs-card-title" style={{ margin: 0 }}><Car size={16} /> {t('Voertuigwaarschuwingen', 'Voertuigwaarschuwingen')}</h3>
            <button data-testid="apk-refresh-rdw-btn" onClick={refreshApkFromRdw} disabled={apkRefreshing}
              title={t('Controleer alle kentekens bij de RDW en werk de APK-datums bij')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--cs-border)', background: 'var(--cs-surface)', color: 'var(--cs-text)', fontSize: 12.5, fontWeight: 600, cursor: apkRefreshing ? 'wait' : 'pointer', opacity: apkRefreshing ? 0.7 : 1 }}>
              <RefreshCw size={14} className={apkRefreshing ? 'spin' : ''} /> {apkRefreshing ? t('Bezig…') : t('APK-datums verversen')}
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '10px 0' }}>
            <AlertChip icon={ShieldCheck} label={t('APK', 'APK')} count={apk30.length} c="#dc2626" onClick={() => onNavigate('apk')} testid="va-apk" />
            <AlertChip icon={Gauge} label={t('Onderhoud', 'Onderhoud')} count={onderhoudOverdue.length} c="#c2410c" onClick={() => onNavigate('apk')} testid="va-onderhoud" />
            <AlertChip icon={AlertOctagon} label={t('Recalls', 'Recalls')} count={recallMatches} c="#7c3aed" onClick={() => onNavigate('apk')} testid="va-recall" />
          </div>
          {apkAlerts.length === 0 ? <div className="cs-empty">{t("Geen APK's vervallen binnenkort.")}</div> : (
            <ul className="apk-list">
              {apkAlerts.slice(0, 6).map(a => (
                <li key={a.kenteken} onClick={() => onNavigate('apk')}>
                  <ShieldCheck size={14} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{a.kenteken} - {a.merk} {a.model}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)' }}>{a.klantNaam} - {t('vervalt')} {new Date(a.apkVervalt).toLocaleDateString(loc)}</div>
                  </div>
                  <span className={`cs-badge ${a.dagen < 14 ? 'red' : a.dagen < 30 ? 'amber' : 'blue'}`}>{a.dagen}d</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* SECTION 7 — QUICK ACTIONS */}
      <div className="cs-card padded" style={{ marginTop: 14 }}>
        <h3 className="cs-card-title"><ClipboardList size={16} /> {t('dash.quick_actions', 'Snelle acties')}</h3>
        <div className="cs-quick-actions">
          <button data-testid="qa-klant" className="qa-btn" onClick={() => onNavigate('klanten')}><UserPlus size={18} /><span className="qa-label">{t('Nieuwe klant')}</span></button>
          <button data-testid="qa-voertuig" className="qa-btn" onClick={() => onNavigate('klanten')}><Car size={18} /><span className="qa-label">{t('Nieuw voertuig', 'Nieuw voertuig')}</span></button>
          <button data-testid="qa-werkorder" className="qa-btn" onClick={() => onNavigate('bonnen')}><Wrench size={18} /><span className="qa-label">{t('Nieuwe werkorder', 'Nieuwe werkorder')}</span></button>
          <button data-testid="qa-bon" className="qa-btn" onClick={() => onNavigate('bonnen')}><FilePlus size={18} /><span className="qa-label">{t('Nieuwe factuur', 'Nieuwe factuur')}</span></button>
          <button data-testid="qa-planning" className="qa-btn" onClick={() => onNavigate('planning')}><CalIcon size={18} /><span className="qa-label">{t('Nieuwe afspraak', 'Nieuwe afspraak')}</span></button>
          <button data-testid="qa-rdw" className="qa-btn" onClick={() => setLookupModal('rdw')}><Search size={18} /><span className="qa-label">{t('RDW check', 'RDW check')}</span></button>
          <button data-testid="qa-vin" className="qa-btn" onClick={() => setLookupModal('vin')}><Fingerprint size={18} /><span className="qa-label">{t('VIN check', 'VIN check')}</span></button>
          <button data-testid="qa-advertentie" className="qa-btn" onClick={() => onNavigate('advertentie')}><Sparkles size={18} /><span className="qa-label">{t('AI advertentie', 'AI advertentie')}</span></button>
          <button data-testid="qa-weekoverzicht" className="qa-btn" onClick={sendWeekSummary} disabled={weekSummaryBusy}><Mail size={18} className={weekSummaryBusy ? 'spin' : ''} /><span className="qa-label">{t('Weekoverzicht mailen', 'Weekoverzicht mailen')}</span></button>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)', marginTop: 8 }}>{t('Je ontvangt elke maandagochtend automatisch een weekoverzicht per e-mail.', 'You receive a weekly summary email every Monday morning.')}</div>
      </div>

      {/* SECTION 8 — RECENT ACTIVITY */}
      <div className="cs-card padded" style={{ marginTop: 14 }} data-testid="card-audit">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3 className="cs-card-title" style={{ margin: 0 }}><ClipboardList size={16} /> {t('Recente activiteit', 'Recente activiteit')}</h3>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {AUDIT_FILTERS.map(([k, lbl]) => (
              <button key={k} onClick={() => setAuditFilter(k)} data-testid={`audit-filter-${k}`}
                style={{ padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid', borderColor: auditFilter === k ? '#2563eb' : 'var(--cs-border)', background: auditFilter === k ? '#2563eb' : '#fff', color: auditFilter === k ? '#fff' : '#64748b' }}>{t(lbl, lbl)}</button>
            ))}
          </div>
        </div>
        <table className="cs-table" style={{ marginTop: 10 }}>
          <thead><tr><th>{t('Datum')}</th><th>{t('Gebruiker')}</th><th>{t('Actie')}</th><th>{t('Detail')}</th></tr></thead>
          <tbody>
            {auditFiltered.slice(0, 10).map((a, i) => (
              <tr key={`${a.id || 'audit'}-${i}`}>
                <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{a.datum}</td>
                <td><span className="cs-badge gray">{a.gebruiker}</span></td>
                <td style={{ fontWeight: 500 }}>{a.actie}</td>
                <td style={{ color: 'var(--cs-text-muted)' }}>{a.detail}</td>
              </tr>
            ))}
            {auditFiltered.length === 0 && <tr><td colSpan={4} className="cs-empty">{t('Geen activiteit voor dit filter.', 'Geen activiteit voor dit filter.')}</td></tr>}
          </tbody>
        </table>
      </div>

      {lookupModal === 'rdw' && <RdwLookupModal onClose={() => setLookupModal(null)} rdwLookup={rdwLookup} />}
      {lookupModal === 'vin' && <VinLookupModal onClose={() => setLookupModal(null)} vinLookup={vinLookup} />}
      {lookupModal === 'adres' && <AdresLookupModal onClose={() => setLookupModal(null)} adresLookup={adresLookup} />}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, accent, onClick, testid, hint }) {
  return (
    <div onClick={onClick} data-testid={testid} className="cs-card" style={{ padding: 14, cursor: 'pointer', borderTop: `3px solid ${accent}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 11.5, fontWeight: 600 }}><Icon size={14} color={accent} /> {label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, color: '#0f172a' }}>{value}</div>
      {hint != null && hint !== '' && (
        <div style={{ fontSize: 11, fontWeight: 700, marginTop: 2, color: accent }}>{hint}</div>
      )}
    </div>
  );
}

function WsCard({ icon: Icon, label, value, c }) {
  return (
    <div style={{ textAlign: 'center', padding: '12px 6px', borderRadius: 10, background: `${c}10`, border: `1px solid ${c}30` }}>
      <Icon size={18} color={c} />
      <div style={{ fontSize: 22, fontWeight: 800, color: c, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 10.5, color: '#475569', marginTop: 2, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function BizStat({ label, value, small }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: small ? 14 : 17, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function AlertChip({ icon: Icon, label, count, c, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, cursor: 'pointer', border: `1px solid ${count > 0 ? c : 'var(--cs-border)'}`, background: count > 0 ? `${c}12` : '#fff', color: count > 0 ? c : '#94a3b8', fontWeight: 700, fontSize: 13 }}>
      <Icon size={14} /> {label} <span style={{ background: count > 0 ? c : '#e2e8f0', color: count > 0 ? '#fff' : '#94a3b8', borderRadius: 999, padding: '0 7px', fontSize: 12 }}>{count}</span>
    </button>
  );
}

function RdwLookupModal({ onClose, rdwLookup }) {
  const { t } = useT();
  const [kt, setKt] = useState(''); const [busy, setBusy] = useState(false); const [result, setResult] = useState(null);
  const submit = (e) => { e?.preventDefault(); if (!kt.trim()) return; setBusy(true); rdwLookup(kt.trim()).then(info => { setResult(info); toast.success(`${t('Gevonden', 'Gevonden')}: ${info.merk} ${info.handelsbenaming}`); }).catch(err => toast.error(t('RDW lookup mislukt'), { description: err.response?.data?.detail || err.message })).finally(() => setBusy(false)); };
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} data-testid="modal-rdw">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}><h3 style={{ margin: 0 }}><Search size={16} style={{ verticalAlign: 'middle' }} /> {t('RDW kenteken check', 'RDW kenteken check')}</h3><button className="cs-btn ghost" onClick={onClose}>×</button></div>
        <form onSubmit={submit} style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <input data-testid="rdw-kenteken" autoFocus className="cs-input" placeholder="12-AB-34" value={kt} onChange={e => setKt(e.target.value.toUpperCase())} style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }} />
          <button data-testid="rdw-submit" type="submit" className="cs-btn primary" disabled={busy || !kt.trim()}>{busy ? t('Bezig...') : t('Opzoeken')}</button>
        </form>
        {result && (
          <div className="cs-card padded" style={{ background: 'var(--cs-card-alt)' }} data-testid="rdw-result">
            <div className="cs-detail-row"><span className="lbl">{t('Merk / Model')}</span><span className="val">{result.merk} {result.handelsbenaming}</span></div>
            <div className="cs-detail-row"><span className="lbl">{t('Bouwjaar')}</span><span className="val">{String(result.datumEersteToelating || '').slice(0, 4) || '-'}</span></div>
            <div className="cs-detail-row"><span className="lbl">{t('APK vervalt')}</span><span className="val">{result.vervaldatumApk ? `${result.vervaldatumApk.slice(0, 4)}-${result.vervaldatumApk.slice(4, 6)}-${result.vervaldatumApk.slice(6, 8)}` : '-'}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}

function VinLookupModal({ onClose, vinLookup }) {
  const { t } = useT();
  const [vin, setVin] = useState(''); const [busy, setBusy] = useState(false); const [result, setResult] = useState(null);
  const submit = (e) => { e?.preventDefault(); if (!vin.trim()) return; setBusy(true); vinLookup(vin.trim()).then(info => { setResult(info); toast.success(`${t('Gevonden', 'Gevonden')}: ${info.merk} ${info.model}`.trim()); }).catch(err => toast.error(t('VIN lookup mislukt'), { description: err.response?.data?.detail || err.message })).finally(() => setBusy(false)); };
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} data-testid="modal-vin">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}><h3 style={{ margin: 0 }}><Fingerprint size={16} style={{ verticalAlign: 'middle' }} /> {t('Chassisnummer check (VIN)', 'Chassisnummer check (VIN)')}</h3><button className="cs-btn ghost" onClick={onClose}>×</button></div>
        <form onSubmit={submit} style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <input data-testid="vin-input" autoFocus className="cs-input" placeholder="WVWZZZ1JZXW000001" maxLength={17} value={vin} onChange={e => setVin(e.target.value.toUpperCase())} style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }} />
          <button data-testid="vin-submit" type="submit" className="cs-btn primary" disabled={busy || !vin.trim()}>{busy ? t('Bezig...') : t('Opzoeken')}</button>
        </form>
        {result && (
          <div className="cs-card padded" style={{ background: 'var(--cs-card-alt)' }} data-testid="vin-result">
            <div className="cs-detail-row"><span className="lbl">{t('Merk / Model')}</span><span className="val">{result.merk} {result.model}</span></div>
            <div className="cs-detail-row"><span className="lbl">{t('Bouwjaar')}</span><span className="val">{result.bouwjaar || '-'}</span></div>
            <div className="cs-detail-row"><span className="lbl">{t('Brandstof')}</span><span className="val">{result.brandstof || result.brandstofRaw || '-'}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}

function AdresLookupModal({ onClose, adresLookup }) {
  const { t } = useT();
  const [pc, setPc] = useState(''); const [hnr, setHnr] = useState(''); const [busy, setBusy] = useState(false); const [result, setResult] = useState(null);
  const submit = (e) => { e?.preventDefault(); if (!pc.trim() || !hnr.trim()) return; setBusy(true); adresLookup(pc.trim(), hnr.trim()).then(a => { setResult(a); toast.success(`${a.straat} ${a.huisnummer}, ${a.postcode} ${a.plaats}`); }).catch(err => toast.error(t('Adres lookup mislukt'), { description: err.response?.data?.detail || err.message })).finally(() => setBusy(false)); };
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} data-testid="modal-adres">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}><h3 style={{ margin: 0 }}><MapPin size={16} style={{ verticalAlign: 'middle' }} /> {t('Adres opzoeken', 'Adres opzoeken')}</h3><button className="cs-btn ghost" onClick={onClose}>×</button></div>
        <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6, marginBottom: 12 }}>
          <input data-testid="adres-postcode" autoFocus className="cs-input" placeholder={t('Postcode (1011 AA)', 'Postcode (1011 AA)')} value={pc} onChange={e => setPc(e.target.value)} />
          <input data-testid="adres-huisnummer" className="cs-input" placeholder={t('Huisnummer', 'Huisnummer')} value={hnr} onChange={e => setHnr(e.target.value)} />
          <button data-testid="adres-submit" type="submit" className="cs-btn primary" disabled={busy || !pc || !hnr}>{busy ? t('Bezig...') : t('Opzoeken')}</button>
        </form>
        {result && (
          <div className="cs-card padded" style={{ background: 'var(--cs-card-alt)' }} data-testid="adres-result">
            <div className="cs-detail-row"><span className="lbl">{t('Straat + nr', 'Straat + nr')}</span><span className="val" style={{ fontWeight: 700 }}>{result.straat} {result.huisnummer}</span></div>
            <div className="cs-detail-row"><span className="lbl">{t('Plaats')}</span><span className="val">{result.plaats}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}
